import { formatContactDisplayName, contactTileEmailLine, contactIsDeceased, type Contact } from "@domaddress/shared";
import DeceasedMark from "./DeceasedMark";
import GroupActivityBadges from "./GroupActivityBadges";
import ContactGroupBadges from "./ContactGroupBadges";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

interface Props {
  contact: Contact;
  onEdit?: () => void;
  onDelete?: () => void;
  onView?: () => void;
  /** Secretaris/beheerder/superadmin: knop om toe te voegen aan e-mail met vCards */
  onAddToSendList?: () => void;
  /** Optioneel: label van de primaire adresboek-lijst (bijv. op “alle contacten”) */
  primaryListLabel?: string;
}

/** Compact row layout for the contacts list view (same data as ContactCard). */
export default function ContactListRow({ contact, onEdit, onDelete, onView, onAddToSendList, primaryListLabel }: Props) {
  const initials = `${contact.first_name[0] ?? ""}${contact.last_name[0] ?? ""}`.toUpperCase();
  const location = [contact.city, contact.country].filter(Boolean).join(", ");
  const emailLine = contactTileEmailLine(contact);
  const deceased = contactIsDeceased(contact);

  return (
    <div
      className={`data-list-row contact-list-row${onView ? " data-list-row--clickable" : ""}${deceased ? " contact-list-row--deceased" : ""}`}
      onClick={onView}
      onKeyDown={
        onView
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onView();
              }
            }
          : undefined
      }
      role={onView ? "button" : undefined}
      tabIndex={onView ? 0 : undefined}
    >
      <div className="data-list-row-main">
        <div className="contact-avatar contact-avatar--sm">
          {contact.profile_picture ? (
            <img src={`${API_URL}${contact.profile_picture}`} alt="" />
          ) : (
            <span>{initials}</span>
          )}
        </div>
        <div className="data-list-row-text">
          <div className="data-list-row-title" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {formatContactDisplayName(contact)}
            {deceased ? <DeceasedMark /> : null}
          </div>
          <div className="data-list-row-meta">
            {emailLine && <span>{emailLine}</span>}
            {contact.phone && <span>vast: {contact.phone}</span>}
            {contact.phone_mobile && <span>mobiel: {contact.phone_mobile}</span>}
            {location && <span className="data-list-row-muted">{location}</span>}
          </div>
        </div>
      </div>
      <div className="data-list-row-badges">
        {primaryListLabel && (
          <span className="group-badge" title="Primaire adresboek-lijst">
            {primaryListLabel}
          </span>
        )}
        <GroupActivityBadges contact={contact} />
        {contact.bezoekersbestand_id != null && (
          <span className="group-badge" title="Komt uit het bezoekersbestand">
            Bezoeker #{contact.bezoekersbestand_id}
          </span>
        )}
        <ContactGroupBadges contact={contact} />
      </div>
      <div className="data-list-row-actions" onClick={(e) => e.stopPropagation()}>
        {onAddToSendList && (
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={onAddToSendList}
            title="Toevoegen aan deze e-mail"
            style={{
              fontWeight: 700,
              minWidth: "7.5rem",
              marginRight: 4,
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
            }}
          >
            Toevoegen →
          </button>
        )}
        {onEdit && (
          <button type="button" className="btn-icon" onClick={onEdit} title="Bewerken">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
            </svg>
          </button>
        )}
        {onDelete && (
          <button type="button" className="btn-icon btn-icon-danger" onClick={onDelete} title="Delete">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3,6 5,6 21,6" />
              <path d="M19,6l-1,14a2,2,0,0,1-2,2H8a2,2,0,0,1-2-2L5,6" />
              <path d="M10,11v6" />
              <path d="M14,11v6" />
              <path d="M9,6V4h6v2" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
